import type {
  AmendedReviewScreeningType,
  AmendedReviewValidation,
  PaginatedResponse,
} from "types/DataTypes";

import { api } from "./api.ts";

type GetAmendedReviewsArgs = {
  msa?: string;
  fileId?: string;
  searchTerm?: string;
};

type GetAmendedReviewsByFileIdArgs = {
  fileId: string;
};

type PatchAmendedReviewArgs = {
  id: string;
  data: Partial<AmendedReviewScreeningType>;
};

type GetAmendedReviewByIdArgs = {
  reviewId: string;
};

type GetARScreeningAuditHistoryArgs = {
  id: string;
};

export const amendedReviewApi = api.injectEndpoints({
  endpoints: (build) => ({
    getAmendedReviews: build.query<unknown, GetAmendedReviewsArgs>({
      query: ({ msa, fileId, searchTerm }) => {
        const params = new URLSearchParams({ ordering: "-created" });
        if (msa) params.append("msa", msa);
        if (fileId) params.append("file__id", fileId);
        return {
          url: `api/v1/amended-review-screenings/?${params.toString()}`,
          method: "GET",
          params: searchTerm ? { filter: searchTerm } : undefined,
        };
      },
      providesTags: ["AmendedScreeningTable"],
    }),
    getAmendedReviewsByFileId: build.query<
      PaginatedResponse<AmendedReviewScreeningType>,
      GetAmendedReviewsByFileIdArgs
    >({
      query: ({ fileId }) => ({
        url: `api/v1/amended-review-screenings/?file__id=${fileId}&include_validations=true`,
        method: "GET",
      }),
      providesTags: (_result, _error, { fileId }) => [
        { type: "AmendedReviewScreening", id: `FILE-${fileId}` },
        { type: "AmendedReviewScreeningValidation", id: `FILE-${fileId}` },
      ],
    }),
    patchAmendedReviewById: build.mutation<
      AmendedReviewScreeningType,
      PatchAmendedReviewArgs
    >({
      query: ({ id, data }) => ({
        url: `api/v1/amended-review-screenings/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [
        "AmendedReviewScreening",
        "AmendedReviewScreeningValidation",
      ],
    }),
    getAmendedReviewById: build.query<
      AmendedReviewScreeningType,
      GetAmendedReviewByIdArgs
    >({
      query: ({ reviewId }) => ({
        url: `api/v1/amended-review-screenings/${reviewId}/`,
        method: "GET",
        params: {},
      }),
      providesTags: ["AmendedReviewScreening"],
    }),
    getAmendedReviewValidationsById: build.query<
      AmendedReviewValidation,
      GetAmendedReviewByIdArgs
    >({
      query: ({ reviewId }) => ({
        url: `api/v1/amended-review-screenings/${reviewId}/validate/`,
        method: "GET",
        params: {},
      }),
      providesTags: ["AmendedReviewScreeningValidation"],
    }),
    getARScreeningAuditHistory: build.query<
      unknown,
      GetARScreeningAuditHistoryArgs
    >({
      query: ({ id }) => ({
        url: `api/v1/amended-review-screenings/${id}/audit_trail/`,
        method: "GET",
      }),
      providesTags: ["AmendedReviewScreening"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAmendedReviewsQuery,
  usePatchAmendedReviewByIdMutation,
  useGetAmendedReviewByIdQuery,
  useGetAmendedReviewValidationsByIdQuery,
  useGetAmendedReviewsByFileIdQuery,
  useGetARScreeningAuditHistoryQuery,
} = amendedReviewApi;
